import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  IconBadge,
  PaymentMethod,
  RoomDetailsService,
} from '@core/services/room-details/room-details.service';
import { PrepaidRoomCluster } from '@core/models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import { PriceService, ReqService } from '@core/services';
import { MealPlan } from '@core/models/MealPlan/meal-plan.enum';
import { RoomPrice } from '@shared/enums/room-price.enum';
import { DisneyRoomCluster } from '@core/models/disney-room-cluster/DisneyRoomCluster.model';
import { DISNEY_DINING_CARD_CODES, DISNEY_FREE_MEAL_PLAN_CODES } from '@shared/constants/disney-offer-codes';
import { CurrencyPipe } from '@shared/pipes/currency.pipe';
import { DisneySupplementsComponent } from './disney-supplements/disney-supplements.component';
import { MealPlansComponent } from './meal-plans/meal-plans.component';
import { PaymentMethodsComponent } from './payment-methods/payment-methods.component';
import { RoomInformationComponent } from './room-information/room-information.component';
import { MbIconModule } from '@am/moebius';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';

@Component({
  selector: 'am-room-summary',
  templateUrl: './room-summary.component.html',
  styleUrls: ['./room-summary.component.scss'],
  standalone: true,
  imports: [
    MbIconModule,
    RoomInformationComponent,
    NgTemplateOutlet,
    PaymentMethodsComponent,
    MealPlansComponent,
    DisneySupplementsComponent,
    AsyncPipe,
    CurrencyPipe,
  ],
})
export class RoomSummaryComponent implements OnInit {
  public readonly ROOM_PRICE = RoomPrice;
  public cluster: PrepaidRoomCluster;
  public bestPrice: number;
  public priceBy: RoomPrice;
  public isMultiRoom = false;
  private paymentMethods: Subject<IconBadge<PaymentMethod>[]> = new BehaviorSubject([]);
  private mealPlans: Subject<IconBadge<MealPlan>[]> = new BehaviorSubject([]);
  private disneySupplements: Subject<IconBadge<string>[]> = new BehaviorSubject([]);
  private isDisney: Subject<boolean> = new BehaviorSubject(false);

  @Input()
  public expanded = false;

  @Output()
  public clickIcon: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private readonly roomDetailsService: RoomDetailsService,
    private readonly priceService: PriceService,
    private readonly contextService: ReqService,
  ) {
    this.priceBy = this.contextService.priceBy;
    this.isDisney.next(this.contextService.isDisneyHotel());
  }

  ngOnInit(): void { }

  @Input()
  public set room(cluster: PrepaidRoomCluster) {
    this.cluster = cluster;
    this.isMultiRoom = cluster.rooms && cluster.rooms.length > 1;
    this.paymentMethods.next(
      this.roomDetailsService.getPaymentMethods(
        cluster.rates,
        this.contextService.getHotelStaticInfo().country,
      ),
    );

    this.mealPlans.next(this.roomDetailsService.getMealPlans(cluster.rates));
    if (this.contextService.isDisneyHotel()) {
      this.disneySupplements.next(
        this.roomDetailsService.getDisneySupplements(cluster as DisneyRoomCluster),
      );
    }
    this.bestPrice = this.priceService.getPrice(
      cluster.rates[0],
      this.priceBy,
      undefined,
      this.contextService.isDisneyHotel(),
    );
  }

  onClick() {
    this.clickIcon.emit(true);
  }

  get paymentMethods$(): Observable<IconBadge<PaymentMethod>[]> {
    return this.paymentMethods.asObservable();
  }

  get mealPlans$(): Observable<IconBadge<MealPlan>[]> {
    return this.mealPlans.asObservable();
  }

  get disneySupplements$(): Observable<IconBadge<string>[]> {
    return this.disneySupplements.asObservable();
  }

  get isDisney$(): Observable<boolean> {
    return this.isDisney.asObservable();
  }

  get hasDiningCard(): boolean {
    const offerCode = (this.cluster.rooms[0] as any)?.offerCode;
    return this.contextService.isDisneyHotel() &&
      (DISNEY_DINING_CARD_CODES.includes(offerCode) || DISNEY_FREE_MEAL_PLAN_CODES.includes(offerCode));
  }

  get diningCardTitle(): string {
    const offerCode = (this.cluster.rooms[0] as any)?.offerCode;
    if (DISNEY_FREE_MEAL_PLAN_CODES.includes(offerCode)) {
      return 'Habitación con planes de comidas gratuitos!';
    }
    return 'Habitación con Disney Dining Card gratuita!';
  }

  get isFreeMealPlan(): boolean {
    const offerCode = (this.cluster.rooms[0] as any)?.offerCode;
    return DISNEY_FREE_MEAL_PLAN_CODES.includes(offerCode);
  }

  get offerCode(): string {
    return (this.cluster.rooms[0] as any)?.offerCode;
  }
}
